import { Controller } from "stimulus"

// RaindancerAuthController
// Gestisce via AJAX tutte le azioni della view manage_auth_view (login, info, rinnovo, logout,
// token macchina, lista dispositivi, connessione profilo): le risposte non aprono piu' una
// pagina JSON ma aggiornano lo stato nella pagina e finiscono nel pannello "Risposta".
export default class extends Controller {
  static targets = [
    "apiToken", "apiExpires", "apiBadge",
    "outToken", "outExpires", "outBadge", "outClient",
    "userUsername", "userClient", "userTempPassword", "userBadge",
    "tokenInput", "output", "outputMeta", "outputEmpty"
  ]

  connect() {
    this.refreshState()
  }

  // ––– Azioni (una per ogni form/bottone) –––

  // devices/token — genera il token con cui Raindancer chiama noi per questa macchina
  generateMachineToken(event) {
    event.preventDefault()
    const form = event.currentTarget
    const fd = new FormData(form)
    fd.append('device_uuid', this.data.get('machine-uuid'))

    this.request({
      label: 'Token macchina (devices/token)',
      method: 'POST',
      url: this.data.get('token-endpoint'),
      headers: { 'X-Api-Key': this.data.get('api-key') },
      body: fd,
      button: this.submitter(event),
      onSuccess: (body) => {
        this.setToken('api', body.raindancer_token, body.raindancer_token_expires_at)
      }
    })
  }

  // auth/create — login su Raindancer, il token viene salvato sulla macchina
  login(event) {
    event.preventDefault()
    const fd = new FormData(event.currentTarget)
    fd.append('machine_uuid', this.data.get('machine-uuid'))

    this.request({
      label: 'Login Raindancer (auth/create)',
      method: 'POST',
      url: this.data.get('create-endpoint'),
      body: fd,
      button: this.submitter(event),
      onSuccess: (body) => {
        this.setToken('out', body.token, body.expireson)
        if (this.hasOutClientTarget) this.outClientTarget.textContent = fd.get('client_no') || '—'
      }
    })
  }

  // auth/info — verifica il token (quello inserito oppure quello salvato sulla macchina)
  info(event) {
    event.preventDefault()
    this.request({
      label: 'Info token (auth/info)',
      method: 'GET',
      url: this.data.get('info-endpoint'),
      query: this.tokenQuery(),
      button: event.currentTarget
    })
  }

  // auth/renew — rinnova il token
  renew(event) {
    event.preventDefault()
    const fd = new FormData()
    Object.entries(this.tokenQuery()).forEach(([k, v]) => fd.append(k, v))

    this.request({
      label: 'Rinnovo token (auth/renew)',
      method: 'POST',
      url: this.data.get('renew-endpoint'),
      body: fd,
      button: event.currentTarget,
      onSuccess: (body) => this.setToken('out', body.token, body.expireson)
    })
  }

  // auth/dispose — revoca il token
  dispose(event) {
    event.preventDefault()
    if (!window.confirm(this.data.get('dispose-confirm'))) return

    this.request({
      label: 'Logout (auth/dispose)',
      method: 'DELETE',
      url: this.data.get('dispose-endpoint'),
      query: this.tokenQuery(),
      button: event.currentTarget,
      onSuccess: () => this.setToken('out', null, null)
    })
  }

  // devices (getdevices) — lista macchine dell'utente, il token e' quello della macchina
  getDevices(event) {
    event.preventDefault()
    const fd = new FormData()
    fd.append('message[command]', 'getdevices')
    fd.append('Authorization', this.apiTokenValue())

    this.request({
      label: 'Lista dispositivi (getdevices)',
      method: 'POST',
      url: this.data.get('devices-endpoint'),
      headers: { 'X-Api-Key': this.data.get('api-key') },
      body: fd,
      button: event.currentTarget
    })
  }

  // profilo utente: salva credenziali + ottiene token (serve per i webhook inbound)
  connectProfile(event) {
    event.preventDefault()
    const form = event.currentTarget
    const fd = new FormData(form)

    this.request({
      label: 'Connetti profilo (salva credenziali)',
      method: 'POST',
      url: this.data.get('connect-endpoint'),
      body: fd,
      button: this.submitter(event),
      onSuccess: (body) => {
        if (body.user) this.setUser(body.user)
        form.querySelector('[name=raindancer_password]').value = ''
      }
    })
  }

  // profilo utente: revoca e rimuove le credenziali
  disconnectProfile(event) {
    event.preventDefault()
    if (!window.confirm(this.data.get('disconnect-confirm'))) return

    this.request({
      label: 'Disconnetti profilo',
      method: 'DELETE',
      url: this.data.get('disconnect-endpoint'),
      button: event.currentTarget,
      onSuccess: () => this.setUser({ username: null, client_no: null, api_temp_password: null })
    })
  }

  copy(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value || this.targetValue(event.currentTarget.dataset.from)
    if (!value) return this.flash('Niente da copiare', 'orange')

    navigator.clipboard.writeText(value)
      .then(() => this.flash('Copiato negli appunti', 'green'))
      .catch(() => this.flash('Copia non riuscita', 'orange'))
  }

  clearOutput(event) {
    if (event) event.preventDefault()
    this.outputTarget.textContent = ''
    this.outputMetaTarget.innerHTML = ''
    this.outputTarget.hidden = true
    this.outputMetaTarget.hidden = true
    this.outputEmptyTarget.hidden = false
  }

  // ––– Richiesta AJAX generica –––

  request({ label, method, url, headers = {}, body = null, query = null, button = null, onSuccess = null }) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const finalUrl = query && Object.keys(query).length
      ? `${url}${url.includes('?') ? '&' : '?'}${new URLSearchParams(query)}`
      : url
    const started = performance.now()

    this.setBusy(button, true)

    return fetch(finalUrl, {
      method,
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        ...headers
      },
      body
    })
      .then((res) => res.text().then((text) => {
        let parsed = null
        try { parsed = text ? JSON.parse(text) : null } catch (e) { parsed = text }
        return { status: res.status, ok: res.ok, body: parsed }
      }))
      .then(({ status, ok, body: parsed }) => {
        const ms = Math.round(performance.now() - started)
        this.showOutput({ label, method, url: finalUrl, status, ms, body: parsed })

        if (ok) {
          if (onSuccess) onSuccess(parsed && typeof parsed === 'object' ? parsed : {})
          this.flash(this.successMessage(label, parsed), 'green')
        } else {
          this.flash(this.errorMessage(label, status, parsed), 'orange')
        }
      })
      .catch((err) => {
        this.showOutput({ label, method, url: finalUrl, status: 0, ms: Math.round(performance.now() - started), body: { error: err.message } })
        this.flash(`${label}: errore di rete`, 'orange')
      })
      .finally(() => this.setBusy(button, false))
  }

  // ––– Stato in pagina –––

  // Aggiorna token/scadenza/badge di "api" (token macchina) oppure "out" (token verso Raindancer)
  setToken(kind, token, expires) {
    const tokenTarget = kind === 'api' ? 'apiToken' : 'outToken'
    const expiresTarget = kind === 'api' ? 'apiExpires' : 'outExpires'

    if (this[`has${this.cap(tokenTarget)}Target`]) {
      const el = this[`${tokenTarget}Target`]
      el.dataset.value = token || ''
      el.textContent = token || '— Nessun token —'
    }
    if (this[`has${this.cap(expiresTarget)}Target`]) {
      this[`${expiresTarget}Target`].dataset.expires = expires || ''
    }
    this.refreshState()
  }

  setUser({ username, client_no, api_temp_password }) {
    if (this.hasUserUsernameTarget) this.userUsernameTarget.textContent = username || '—'
    if (this.hasUserClientTarget) this.userClientTarget.textContent = client_no || '—'
    if (this.hasUserTempPasswordTarget) {
      this.userTempPasswordTarget.dataset.value = api_temp_password || ''
      this.userTempPasswordTarget.textContent = api_temp_password || '—'
    }
    if (this.hasUserBadgeTarget) {
      this.paintBadge(this.userBadgeTarget, api_temp_password ? 'ok' : 'none', api_temp_password ? 'Connesso' : 'Non connesso')
    }
  }

  // Ricalcola testo e badge delle scadenze a partire da data-expires (ISO)
  refreshState() {
    this.renderExpiry(this.hasApiExpiresTarget && this.apiExpiresTarget, this.hasApiBadgeTarget && this.apiBadgeTarget, this.hasApiTokenTarget && this.apiTokenTarget)
    this.renderExpiry(this.hasOutExpiresTarget && this.outExpiresTarget, this.hasOutBadgeTarget && this.outBadgeTarget, this.hasOutTokenTarget && this.outTokenTarget)
  }

  renderExpiry(expiresEl, badgeEl, tokenEl) {
    if (!expiresEl || !badgeEl) return
    const iso = expiresEl.dataset.expires
    const hasToken = !!(tokenEl && tokenEl.dataset.value)
    const date = iso ? new Date(iso) : null
    const valid = date && !isNaN(date)

    expiresEl.textContent = valid ? date.toLocaleString('it-IT') : '—'

    if (!hasToken) return this.paintBadge(badgeEl, 'none', 'Nessun token')
    if (!valid) return this.paintBadge(badgeEl, 'ok', 'Presente')
    this.paintBadge(badgeEl, date > new Date() ? 'ok' : 'expired', date > new Date() ? 'Valido' : 'Scaduto')
  }

  paintBadge(el, state, text) {
    const colors = { ok: 'c-tag--green', expired: 'c-tag--orange', none: '' }
    el.className = `c-tag ${colors[state] || ''}`.trim()
    el.textContent = text
  }

  // ––– Pannello risposta –––

  showOutput({ label, method, url, status, ms, body }) {
    const ok = status >= 200 && status < 300
    const pretty = body === null || body === ''
      ? '(risposta vuota)'
      : (typeof body === 'string' ? body : JSON.stringify(body, null, 2))

    this.outputEmptyTarget.hidden = true
    this.outputMetaTarget.hidden = false
    this.outputTarget.hidden = false

    this.outputMetaTarget.innerHTML = ''
    const title = document.createElement('strong')
    title.textContent = label
    const tag = document.createElement('span')
    tag.className = `c-tag ${ok ? 'c-tag--green' : 'c-tag--orange'}`
    tag.textContent = status ? `${status}` : 'Errore'
    const detail = document.createElement('span')
    detail.className = 'r-auth__console-detail'
    detail.textContent = `${method} ${url} · ${ms} ms · ${new Date().toLocaleTimeString('it-IT')}`
    this.outputMetaTarget.append(title, tag, detail)

    this.outputTarget.textContent = pretty
    this.outputTarget.classList.toggle('is-error', !ok)
  }

  // ––– Utilita' –––

  flash(message, type) {
    const el = document.getElementById('flash')
    const flash = el && this.application.getControllerForElementAndIdentifier(el, 'flash')
    if (flash) flash.openMessage(message, type)
  }

  successMessage(label, body) {
    return (body && body.message) ? body.message : `${label}: ok`
  }

  errorMessage(label, status, body) {
    const detail = body && typeof body === 'object'
      ? (typeof body.error === 'string' ? body.error : (body.message || ''))
      : ''
    return `${label}: errore ${status}${detail ? ' — ' + detail : ''}`
  }

  setBusy(button, busy) {
    if (!button) return
    button.disabled = busy
    button.classList.toggle('is-loading', busy)
  }

  // Il bottone che ha inviato il form (per mostrare lo stato di caricamento)
  submitter(event) {
    return event.submitter || event.currentTarget.querySelector('[type=submit]')
  }

  tokenQuery() {
    const q = { machine_uuid: this.data.get('machine-uuid') }
    const typed = this.hasTokenInputTarget ? this.tokenInputTarget.value.trim() : ''
    if (typed) q.token = typed
    return q
  }

  apiTokenValue() {
    return this.hasApiTokenTarget ? (this.apiTokenTarget.dataset.value || '') : ''
  }

  targetValue(targetName) {
    const el = this[`${targetName}Target`]
    return el ? el.dataset.value : ''
  }

  cap(s) {
    return s.charAt(0).toUpperCase() + s.slice(1)
  }
}
